import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "tt_cookie_consent";
const CONSENT_VERSION = 1;
const CONSENT_EVENT = "tt-consent-changed";
const OPEN_SETTINGS_EVENT = "open-cookie-settings";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.analytics === "boolean" &&
      parsed.necessary === true
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeConsent(analytics) {
  const consent = {
    necessary: true,
    analytics: !!analytics,
    timestamp: Date.now(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // ignore storage errors (private mode, etc.)
  }
  try {
    window.dispatchEvent(
      new CustomEvent(CONSENT_EVENT, { detail: consent })
    );
  } catch {
    // ignore in environments without CustomEvent
  }
  return consent;
}

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);

  // Mount: decide whether to show banner and seed analytics toggle
  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setAnalyticsOn(!!stored.analytics);
      setShowBanner(false);
    } else {
      setAnalyticsOn(false);
      setShowBanner(true);
    }
  }, []);

  // Listen for external "open-cookie-settings" event
  useEffect(() => {
    const handler = () => {
      const stored = readConsent();
      setAnalyticsOn(stored ? !!stored.analytics : false);
      setShowSettings(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, handler);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, handler);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    if (!showSettings) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showSettings]);

  // Escape to close modal
  useEffect(() => {
    if (!showSettings) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setShowSettings(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showSettings]);

  const acceptAll = useCallback(() => {
    writeConsent(true);
    setAnalyticsOn(true);
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const rejectAll = useCallback(() => {
    writeConsent(false);
    setAnalyticsOn(false);
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const saveCurrent = useCallback(() => {
    writeConsent(analyticsOn);
    setShowBanner(false);
    setShowSettings(false);
  }, [analyticsOn]);

  const openSettings = useCallback(() => setShowSettings(true), []);
  const closeSettings = useCallback(() => setShowSettings(false), []);

  return (
    <>
      <style>{`
        @keyframes tt-banner-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes tt-modal-in {
          from { opacity: 0; transform: scale(.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes tt-backdrop-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .tt-cookie-banner, .tt-cookie-modal-panel, .tt-cookie-backdrop {
            animation: none !important;
            transform: none !important;
          }
          .tt-toggle-track, .tt-toggle-knob {
            transition: none !important;
          }
        }
      `}</style>

      {showBanner && (
        <Banner
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onOpenSettings={openSettings}
        />
      )}

      {showSettings && (
        <SettingsModal
          analyticsOn={analyticsOn}
          setAnalyticsOn={setAnalyticsOn}
          onClose={closeSettings}
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onSave={saveCurrent}
        />
      )}
    </>
  );
}

/* ---------- Banner ---------- */

function Banner({ onAcceptAll, onRejectAll, onOpenSettings }) {
  const { t } = useTranslation();
  return (
    <div
      role="region"
      aria-label={t("cookie.regionAria")}
      className="tt-cookie-banner"
      style={bannerWrapStyle}
    >
      <div style={bannerCardStyle}>
        <button
          type="button"
          aria-label={t("cookie.banner.closeAria")}
          onClick={onRejectAll}
          style={bannerCloseStyle}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 style={bannerTitleStyle}>
          {t("cookie.banner.title")}
        </h2>

        <p style={bannerBodyStyle}>
          {t("cookie.banner.body")}
        </p>

        <div style={bannerButtonRowStyle}>
          <button
            type="button"
            onClick={onOpenSettings}
            style={outlineBtnStyle}
          >
            {t("cookie.banner.settings")}
          </button>
          <button
            type="button"
            onClick={onRejectAll}
            style={outlineBtnStyle}
          >
            {t("cookie.banner.rejectAll")}
          </button>
          <button
            type="button"
            onClick={onAcceptAll}
            style={filledGradientBtnStyle}
          >
            {t("cookie.banner.acceptAll")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Settings modal ---------- */

function SettingsModal({
  analyticsOn,
  setAnalyticsOn,
  onClose,
  onAcceptAll,
  onRejectAll,
  onSave,
}) {
  const { t, i18n } = useTranslation();
  const titleId = "tt-cookie-settings-title";
  const closeBtnRef = useRef(null);
  const locale = (i18n && i18n.language) || "en";

  useEffect(() => {
    // Focus the close button on open
    if (closeBtnRef.current) closeBtnRef.current.focus();
  }, []);

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="tt-cookie-backdrop"
      style={backdropStyle}
      onClick={onBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="tt-cookie-modal-panel"
        style={panelStyle}
      >
        <button
          ref={closeBtnRef}
          type="button"
          aria-label={t("cookie.modal.closeAria")}
          onClick={onClose}
          style={panelCloseStyle}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 4L14 14M14 4L4 14"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <h2 id={titleId} style={panelTitleStyle}>
          {t("cookie.modal.title")}
        </h2>

        <p style={panelIntroStyle}>
          {t("cookie.modal.intro")}
        </p>

        {/* A. Strictly Necessary */}
        <Section
          title={t("cookie.modal.necessaryTitle")}
          body={
            <p style={sectionBodyStyle}>
              {t("cookie.modal.necessaryBody")}
            </p>
          }
          control={
            <TogglePill
              on={true}
              disabled
              ariaLabel={t("cookie.modal.necessaryAria")}
            />
          }
        />

        <Divider />

        {/* B. Analytics */}
        <Section
          title={t("cookie.modal.analyticsTitle")}
          body={
            <>
              <p style={sectionBodyStyle}>
                {t("cookie.modal.analyticsBody1")}
              </p>
              <p style={{ ...sectionBodyStyle, marginTop: 10 }}>
                {t("cookie.modal.analyticsBody2")}
              </p>
            </>
          }
          control={
            <TogglePill
              on={analyticsOn}
              onChange={() => setAnalyticsOn((v) => !v)}
              ariaLabel={t("cookie.modal.analyticsAria")}
            />
          }
        />

        <Divider />

        {/* C. Cookie Policy */}
        <div style={{ marginTop: 28 }}>
          <h3 style={sectionTitleStyle}>{t("cookie.modal.policyTitle")}</h3>
          <p style={{ ...sectionBodyStyle, marginTop: 8 }}>
            {t("cookie.modal.policyBody")}{" "}
            <a href={`/${locale}/cookie-policy`} style={inlineLinkStyle}>
              {t("cookie.modal.policyLink")}
            </a>
            .
          </p>
        </div>

        <div style={panelButtonRowStyle}>
          <button
            type="button"
            onClick={onAcceptAll}
            style={persimmonFilledBtnStyle}
          >
            {t("cookie.modal.enableAll")}
          </button>
          <button
            type="button"
            onClick={onRejectAll}
            style={persimmonOutlineBtnStyle}
          >
            {t("cookie.modal.rejectAll")}
          </button>
          <button
            type="button"
            onClick={onSave}
            style={persimmonFilledBtnStyle}
          >
            {t("cookie.modal.saveChanges")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, body, control }) {
  return (
    <div style={sectionRowStyle}>
      <div style={sectionTextStyle}>
        <h3 style={sectionTitleStyle}>{title}</h3>
        <div style={{ marginTop: 8 }}>{body}</div>
      </div>
      <div style={sectionControlStyle}>{control}</div>
    </div>
  );
}

function Divider() {
  return (
    <div
      aria-hidden="true"
      style={{
        height: 1,
        background: "var(--line)",
        marginTop: 24,
      }}
    />
  );
}

/* ---------- Toggle pill ---------- */

function TogglePill({ on, onChange, disabled = false, ariaLabel }) {
  const { t } = useTranslation();
  const trackBg = on ? "#22A06B" : "#C8C4BB";
  const labelColor = on ? "#22A06B" : "var(--muted)";
  const labelText = on ? t("cookie.modal.toggleEnabled") : t("cookie.modal.toggleDisabled");

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        opacity: disabled ? 0.7 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <span
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: 13,
          fontWeight: 600,
          color: labelColor,
          letterSpacing: ".01em",
        }}
      >
        {labelText}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-disabled={disabled || undefined}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : onChange}
        className="tt-toggle-track"
        style={{
          position: "relative",
          width: 44,
          height: 24,
          borderRadius: 99,
          background: trackBg,
          border: "none",
          padding: 0,
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "background .25s ease",
          outline: "none",
          flexShrink: 0,
        }}
      >
        <span
          className="tt-toggle-knob"
          style={{
            position: "absolute",
            top: 3,
            left: on ? 23 : 3,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 1px 3px rgba(16,14,12,.25)",
            transition: "left .25s ease",
          }}
        />
      </button>
    </div>
  );
}

/* ---------- Styles ---------- */

const bannerWrapStyle = {
  position: "fixed",
  zIndex: 9998,
  right: "clamp(12px, 3vw, 24px)",
  left: "clamp(12px, 3vw, 24px)",
  bottom: "clamp(12px, 3vw, 24px)",
  display: "flex",
  justifyContent: "flex-end",
  pointerEvents: "none",
};

const bannerCardStyle = {
  position: "relative",
  width: "100%",
  maxWidth: 460,
  padding: "22px 24px",
  background: "var(--bg)",
  border: "1px solid var(--line)",
  borderRadius: 16,
  boxShadow: "0 30px 60px -20px rgba(16,14,12,.25)",
  pointerEvents: "auto",
  animation: "tt-banner-in .35s ease-out both",
  fontFamily: "Inter, sans-serif",
};

const bannerCloseStyle = {
  position: "absolute",
  top: 12,
  right: 12,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  borderRadius: 999,
  color: "var(--muted)",
  cursor: "pointer",
  padding: 0,
};

const bannerTitleStyle = {
  fontFamily: "Satoshi, Inter, sans-serif",
  fontSize: 16.5,
  fontWeight: 700,
  color: "var(--ink)",
  lineHeight: 1.25,
  letterSpacing: "-.012em",
  margin: 0,
  paddingRight: 28,
};

const bannerBodyStyle = {
  marginTop: 10,
  fontSize: 13,
  lineHeight: 1.55,
  color: "var(--ink-2)",
};

const bannerButtonRowStyle = {
  marginTop: 16,
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
};

const outlineBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1.5px solid var(--line)",
  background: "transparent",
  color: "var(--ink)",
  borderRadius: 99,
  padding: "10px 16px",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "Inter, sans-serif",
  cursor: "pointer",
  lineHeight: 1,
};

const filledGradientBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "linear-gradient(135deg, var(--persimmon), var(--marigold))",
  color: "#fff",
  borderRadius: 99,
  padding: "10px 18px",
  fontSize: 13,
  fontWeight: 700,
  fontFamily: "Inter, sans-serif",
  cursor: "pointer",
  lineHeight: 1,
  boxShadow: "0 8px 20px -8px rgba(255,105,45,.55)",
};

const backdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(16,14,12,.55)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
  animation: "tt-backdrop-in .25s ease-out both",
};

const panelStyle = {
  position: "relative",
  width: "min(92vw, 880px)",
  maxWidth: 880,
  maxHeight: "88vh",
  overflowY: "auto",
  background: "#EFEFEF",
  borderRadius: 18,
  padding: "clamp(24px, 4vw, 36px) clamp(20px, 3vw, 32px)",
  boxShadow: "0 30px 80px -20px rgba(16,14,12,.4)",
  animation: "tt-modal-in .3s ease-out both",
  fontFamily: "Inter, sans-serif",
};

const panelCloseStyle = {
  position: "absolute",
  top: 16,
  right: 16,
  width: 36,
  height: 36,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  borderRadius: 999,
  color: "var(--muted)",
  cursor: "pointer",
  padding: 0,
};

const panelTitleStyle = {
  fontFamily: "Satoshi, Inter, sans-serif",
  fontSize: 28,
  fontWeight: 700,
  color: "var(--ink)",
  letterSpacing: "-.02em",
  lineHeight: 1.1,
  margin: 0,
  paddingRight: 48,
};

const panelIntroStyle = {
  marginTop: 20,
  fontSize: 15,
  lineHeight: 1.6,
  color: "var(--ink-2)",
};

const sectionRowStyle = {
  marginTop: 28,
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 16,
};

const sectionTextStyle = {
  flex: "1 1 320px",
  maxWidth: 640,
  minWidth: 0,
};

const sectionControlStyle = {
  flex: "0 0 auto",
  paddingTop: 4,
};

const sectionTitleStyle = {
  fontFamily: "Satoshi, Inter, sans-serif",
  fontSize: 19,
  fontWeight: 700,
  color: "var(--ink)",
  letterSpacing: "-.012em",
  lineHeight: 1.2,
  margin: 0,
};

const sectionBodyStyle = {
  fontSize: 14.5,
  lineHeight: 1.6,
  color: "var(--ink-2)",
  margin: 0,
};

const inlineLinkStyle = {
  color: "var(--persimmon)",
  textDecoration: "underline",
  textUnderlineOffset: 2,
};

const panelButtonRowStyle = {
  marginTop: 32,
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const persimmonFilledBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1.5px solid var(--persimmon)",
  background: "var(--persimmon)",
  color: "#fff",
  borderRadius: 99,
  padding: "12px 22px",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Inter, sans-serif",
  cursor: "pointer",
  lineHeight: 1,
};

const persimmonOutlineBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1.5px solid var(--persimmon)",
  background: "transparent",
  color: "var(--persimmon)",
  borderRadius: 99,
  padding: "12px 22px",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Inter, sans-serif",
  cursor: "pointer",
  lineHeight: 1,
};
